import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { BookOpenCheck, ArrowRight, Loader2 } from "lucide-react";
import { listWritingCourses } from "@/lib/api/writing-academy.functions";

export const Route = createFileRoute("/writing-academy")({
  head: () => ({
    meta: [
      { title: "Legal Writing Academy — NDLCS" },
      {
        name: "description",
        content:
          "Master South African legal drafting: letters of demand, NCA notices, ombud complaints, pleadings. Real briefs, AI feedback, signed certificates.",
      },
      { property: "og:title", content: "Legal Writing Academy — NDLCS" },
      {
        property: "og:description",
        content:
          "Learn to draft like an SA attorney — graded by AI, signed off by humans.",
      },
    ],
  }),
  component: Page,
});

function Page() {
  const fetchCourses = useServerFn(listWritingCourses);
  const { data, isLoading } = useQuery({
    queryKey: ["writing-courses"],
    queryFn: () => fetchCourses(),
  });

  return (
    <div className="min-h-screen bg-background">
      <section className="border-b bg-gradient-to-br from-primary/10 via-background to-background">
        <div className="container mx-auto px-4 py-16 max-w-5xl">
          <div className="flex items-center gap-2 text-primary font-medium">
            <BookOpenCheck className="size-5" />
            <span className="text-sm uppercase tracking-wider">NDLCS · Legal Writing Academy</span>
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight">
            Draft like a South African attorney.
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
            Short, focused courses on the documents that fill an SA practitioner's day —
            letters of demand, NCA notices, ombud complaints, and pleadings. Submit your
            draft, get AI feedback in seconds, and earn a signed certificate when a
            qualified reviewer signs it off.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <Badge>AI rubric feedback</Badge>
            <Badge>Reviewed by an admitted attorney</Badge>
            <Badge>QR-verifiable certificate</Badge>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-12 max-w-5xl">
        <h2 className="text-2xl font-semibold mb-6">Available courses</h2>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : !data?.courses.length ? (
          <p className="text-muted-foreground">More courses launching soon.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5">
            {data.courses.map((c) => (
              <Link
                key={c.slug}
                to="/portal/writing-academy/$slug"
                params={{ slug: c.slug }}
                className="group rounded-xl border bg-card p-6 hover:border-primary transition-colors"
              >
                <div className="text-xs text-muted-foreground font-mono">{c.code}</div>
                <h3 className="text-xl font-semibold mt-1 group-hover:text-primary">
                  {c.title}
                </h3>
                <p className="text-sm text-muted-foreground mt-2 line-clamp-3">
                  {c.summary}
                </p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm text-primary font-medium">
                  Start course <ArrowRight className="size-4" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full border bg-card px-3 py-1 text-xs font-medium">
      {children}
    </span>
  );
}
